/* eslint-disable @typescript-eslint/no-explicit-any */
import { useContext, useRef, useState } from 'react';

import RequestContext from './RequestContext.ts';
import { CRITICAL_ALERT, WARNING_ALERT, CLEAR_ALERT } from '../../Alert/AlertContext.ts';
import { useDispatchAlert } from '../../Alert/AlertManager.tsx';
import useEventCallback from '../useEventCallback/index.ts';
import uuid from '../uuid/index.ts';

const STATES = {
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE'
};

export const HAS_ACTIVE_REQUEST = 'HAS_ACTIVE_REQUEST';
export const FAILED_REQUEST = 'FAILED_REQUEST';
export interface UseRequestOptions {
  /**
   * Allows assigning an id to what this request does
   */
  requestId?: string;
}

/**
 * Returns the same function that was inputted in callback
 */
type RequestResponse<T extends (...args: any[]) => any> = (
  ...args: Parameters<T>
) => ReturnType<T> | typeof HAS_ACTIVE_REQUEST | typeof FAILED_REQUEST;

/**
 * Automatic reporting of errors to AlertManager
 * If used in conjunction with RequestContext will not allow user to trigger dangerous actions twice
 * @param callback A callback function that typically is async
 */
export default function useRequest<T extends (...args: any[]) => any>(
  callback: T,
  { requestId }: UseRequestOptions = {}
): RequestResponse<T> {
  const [id] = useState(requestId || uuid());
  const { isRequestActive, onRequestStart, onRequestEnd } = useContext(RequestContext);
  const dispatchAlert = useDispatchAlert();
  const fn = useEventCallback(callback);
  const state = useRef(STATES.INACTIVE);
  return useEventCallback((...args: Parameters<T>) => {
    if (state.current === STATES.ACTIVE || isRequestActive(id)) {
      dispatchAlert({
        message: 'Please wait for previous request to complete',
        id: HAS_ACTIVE_REQUEST,
        type: WARNING_ALERT
      });

      return HAS_ACTIVE_REQUEST;
    }

    state.current = STATES.ACTIVE;

    const clearActiveRequest = (err?: Error) => {
      state.current = STATES.INACTIVE;
      dispatchAlert({ type: CLEAR_ALERT, id: HAS_ACTIVE_REQUEST });
      onRequestEnd(id, err);
    };
    const handleError = (error: Error) => {
      clearActiveRequest(error);
      dispatchAlert({
        message: error.message,
        id: error.message,
        type: CRITICAL_ALERT
      });
    };

    try {
      onRequestStart(id);
      const toResolve = fn(...args);

      Promise.resolve(toResolve)
        .catch(handleError)
        .then(() => clearActiveRequest());

      return toResolve;
    } catch (e) {
      handleError(e as Error);

      return FAILED_REQUEST;
    }
  });
}
